// Recursive editor for the scoring condition DSL.
// Spec: apps/api/src/scoring/conditions.ts
// DSL: {field, op, value} | {all: [...]} | {any: [...]} | {not: ...}
//
// Strategy: a node is either a leaf (one {field, op, value}) or a
// group (all|any|not). The editor lets you switch a leaf to a group
// and back, and recurse. We render a small tree with +/× buttons.
//
// "value" is JSON-typed (so the user can write ["a", "b"] for the
// "in" op, true/false, numbers, etc). We keep a text input and
// parse on blur. Null value = no value (legal for "exists" op).

import { useState } from 'react';
import type { ConditionLeaf, ConditionNode, ConditionOp } from './types';

const OP_OPTIONS: Array<{ value: ConditionOp; label: string }> = [
  { value: 'eq', label: '= equals' },
  { value: 'neq', label: '≠ not equals' },
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '≥ greater or equal' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '≤ less or equal' },
  { value: 'in', label: 'in [array]' },
  { value: 'contains', label: 'contains' },
  { value: 'exists', label: 'exists (any value)' },
];

export function ConditionBuilder({
  value,
  onChange,
}: {
  value: ConditionNode | null;
  onChange: (next: ConditionNode | null) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-dim">
          Leave empty to match every event of this type.
        </p>
        {value ? (
          <button type="button" className="btn" onClick={() => onChange(null)}>
            Clear
          </button>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => onChange({ all: [] })}
          >
            Add condition
          </button>
        )}
      </div>
      {value ? (
        <NodeEditor value={value} onChange={onChange} depth={0} />
      ) : null}
    </div>
  );
}

type GroupOp = 'all' | 'any' | 'not';

function NodeEditor({
  value,
  onChange,
  depth,
}: {
  value: ConditionNode;
  onChange: (next: ConditionNode) => void;
  depth: number;
}) {
  if (isLeaf(value)) {
    return <LeafEditor leaf={value} onChange={onChange} depth={depth} />;
  }
  // Group node
  const groupOp: GroupOp = isAll(value) ? 'all' : isAny(value) ? 'any' : 'not';
  let children: ConditionNode[];
  if (groupOp === 'all' && isAll(value)) {
    children = value.all;
  } else if (groupOp === 'any' && isAny(value)) {
    children = value.any;
  } else if (isNot(value)) {
    children = [value.not];
  } else {
    children = [];
  }

  return (
    <div
      className="rounded-md border border-white/10 bg-bg-elevated/40 p-2"
      style={{ marginLeft: `${depth * 12}px` }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input w-auto"
          value={groupOp}
          onChange={(e) => {
            const next = e.target.value as GroupOp;
            if (next === 'all') onChange({ all: children });
            if (next === 'any') onChange({ any: children });
            if (next === 'not') onChange({ not: children[0] ?? { all: [] } });
          }}
        >
          <option value="all">ALL of</option>
          <option value="any">ANY of</option>
          <option value="not">NOT</option>
        </select>
        <span className="text-xs text-ink-dim">
          {groupOp === 'all' ? 'every child must match' : groupOp === 'any' ? 'at least one child must match' : 'child must NOT match'}
        </span>
        {groupOp !== 'not' ? (
          <>
            <button
              type="button"
              className="btn"
              onClick={() => {
                const newChild: ConditionLeaf = { field: 'payload.action', op: 'eq', value: '' };
                const arr = [...children, newChild];
                if (groupOp === 'all') onChange({ all: arr });
                else onChange({ any: arr });
              }}
            >
              + Add
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => onChange({ field: 'type', op: 'eq', value: '' } as ConditionLeaf)}
            >
              To leaf
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => onChange({ all: [children[0] ?? { field: 'type', op: 'eq', value: '' }] })}
          >
            To group
          </button>
        )}
      </div>
      <div className="mt-2 space-y-2">
        {children.map((c, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1">
              <NodeEditor
                value={c}
                onChange={(next) => {
                  if (groupOp === 'not') {
                    onChange({ not: next });
                  } else {
                    const arr = [...children];
                    arr[i] = next;
                    if (groupOp === 'all') onChange({ all: arr });
                    else onChange({ any: arr });
                  }
                }}
                depth={depth + 1}
              />
            </div>
            {groupOp !== 'not' && children.length > 1 ? (
              <button
                type="button"
                className="btn-danger"
                onClick={() => {
                  const arr = children.filter((_, j) => j !== i);
                  if (groupOp === 'all') onChange({ all: arr });
                  else onChange({ any: arr });
                }}
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeafEditor({
  leaf,
  onChange,
  depth,
}: {
  leaf: ConditionLeaf;
  onChange: (next: ConditionNode) => void;
  depth: number;
}) {
  const [valueText, setValueText] = useState(() => formatValue(leaf.value));
  const [valueErr, setValueErr] = useState<string | null>(null);

  return (
    <div
      className="rounded-md border border-white/10 bg-bg-elevated/40 p-2"
      style={{ marginLeft: `${depth * 12}px` }}
    >
      <div className="grid gap-2 sm:grid-cols-12">
        <div className="sm:col-span-4">
          <label className="label">Field</label>
          <input
            className="input font-mono"
            value={leaf.field}
            placeholder="comment_body  (no 'payload.' prefix)"
            onChange={(e) => onChange({ ...leaf, field: e.target.value })}
          />
        </div>
        <div className="sm:col-span-3">
          <label className="label">Op</label>
          <select
            className="input"
            value={leaf.op}
            onChange={(e) => onChange({ ...leaf, op: e.target.value as ConditionOp })}
          >
            {OP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-4">
          <label className="label">Value (JSON or string)</label>
          <input
            className="input font-mono"
            value={valueText}
            placeholder={leaf.op === 'exists' ? '(not used)' : '"opened" or [1,2,3] or true'}
            onChange={(e) => setValueText(e.target.value)}
            onBlur={() => {
              if (leaf.op === 'exists' || valueText.trim() === '') {
                setValueErr(null);
                onChange({ ...leaf, value: undefined });
                return;
              }
              try {
                // Try JSON first; fall back to string.
                const parsed = JSON.parse(valueText);
                setValueErr(null);
                onChange({ ...leaf, value: parsed });
              } catch {
                onChange({ ...leaf, value: valueText });
                setValueErr(null);
              }
            }}
          />
        </div>
        <div className="flex items-end sm:col-span-1">
          <button
            type="button"
            className="btn"
            onClick={() => onChange({ all: [leaf] })}
            title="Convert to group"
          >
            ↳
          </button>
        </div>
      </div>
      {valueErr ? <p className="mt-1 text-xs text-accent-danger">{valueErr}</p> : null}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function isLeaf(v: ConditionNode): v is ConditionLeaf {
  return 'field' in v && 'op' in v;
}
function isAll(v: ConditionNode): v is { all: ConditionNode[] } {
  return 'all' in v;
}
function isAny(v: ConditionNode): v is { any: ConditionNode[] } {
  return 'any' in v;
}
function isNot(v: ConditionNode): v is { not: ConditionNode } {
  return 'not' in v;
}
